import React, { useState, useEffect } from 'react';
import dashboardService from '../../services/dashboardService';
import conversationService from '../../services/conversationService';

/**
 * Improved Pending Review Section component for the dashboard
 * Compact when empty, informative when populated
 */
const PendingReviewSection = ({ className = '' }) => {
  const [pendingConversations, setPendingConversations] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedConversation, setExpandedConversation] = useState(null);

  // Load pending conversations on component mount
  useEffect(() => {
    loadPendingConversations();
  }, []);

  // Load pending conversations from the API
  const loadPendingConversations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await dashboardService.getPendingReviewConversations(5); // Show top 5 in dashboard
      
      if (response.conversations) {
        setPendingConversations(response.conversations);
        setTotalPending(response.total_pending);
      } else {
        setPendingConversations([]);
        setTotalPending(0);
      }
    } catch (error) {
      console.error('Error loading pending conversations:', error);
      setError('Failed to load pending conversations');
      setPendingConversations([]);
      setTotalPending(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (conversationId, newStatus) => {
    try {
      await conversationService.updateConversationStatus(conversationId, newStatus);
      loadPendingConversations(); // Reload to reflect changes
    } catch (error) {
      console.error('Error updating conversation status:', error);
      setError('Failed to update conversation status');
    }
  };

  // Get priority styling based on days pending
  const getPriorityColor = (daysPending) => {
    if (daysPending >= 14) return 'bg-red-900 text-red-300 border-red-700';
    if (daysPending >= 7) return 'bg-yellow-900 text-yellow-300 border-yellow-700';
    return 'bg-blue-900 text-blue-300 border-blue-700';
  };

  const getPriorityLabel = (daysPending) => {
    if (daysPending >= 14) return 'High Priority';
    if (daysPending >= 7) return 'Medium Priority';
    return 'Low Priority';
  };

  // Compact version when no pending conversations
  if (!isLoading && !error && pendingConversations.length === 0) {
    return (
      <div className={`sm-card-static p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-green-900 mr-3">
              <svg className="h-5 w-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">Pending Review</h3>
              <p className="text-xs text-gray-400">All conversations up to date</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-lg font-bold text-green-400">0</span>
            <p className="text-xs text-gray-400">Pending</p>
          </div>
        </div>
      </div>
    );
  }

  // Full version when there are pending conversations or loading/error states
  return (
    <div className={`sm-card-static ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-yellow-900 mr-3">
              <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white">Pending Review</h3>
              <p className="text-sm text-gray-400">Conversations requiring manual attention</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-yellow-400">{totalPending}</span>
            <p className="text-xs text-gray-400">Total Pending</p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
          <span className="ml-3 text-gray-400 text-sm">Loading pending conversations...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="m-4 bg-red-900 border border-red-700 text-red-300 p-3 rounded" role="alert">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Pending Conversations List */}
      {!isLoading && !error && pendingConversations.length > 0 && (
        <div className="divide-y divide-gray-700">
          {pendingConversations.map((conversation) => (
            <div key={conversation.id} className="p-4 hover:bg-gray-700/50 transition-colors motion-reduce:transition-none">
              {/* Conversation Header */}
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`px-2 py-1 text-xs font-medium rounded border ${getPriorityColor(conversation.days_pending)}`}>
                    {getPriorityLabel(conversation.days_pending)}
                  </div>
                  <span className="text-sm text-gray-400">
                    {conversation.days_pending} day{conversation.days_pending !== 1 ? 's' : ''} pending
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  ID: {conversation.id}
                </div>
              </div>

              {/* User Info */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-600">
                  <svg className="h-3 w-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-sm text-white font-medium">
                  {conversation.user_name || 'Anonymous User'}
                </span>
                {conversation.user_email && (
                  <span className="text-xs text-gray-400">({conversation.user_email})</span>
                )}
              </div>

              {/* Question Preview */}
              <div className="mb-3">
                <p className="text-sm text-gray-300 line-clamp-2">
                  <span className="font-medium">Q:</span> {conversation.question}
                </p>
                {expandedConversation === conversation.id && conversation.answer && (
                  <div className="mt-2 p-2 bg-gray-700 rounded text-sm text-gray-300">
                    <span className="font-medium">A:</span> {conversation.answer}
                  </div>
                )}
              </div>

              {/* Status Notes */}
              {conversation.status_notes && (
                <div className="mb-3 p-2 bg-gray-700 rounded">
                  <p className="text-xs text-gray-400 font-medium mb-1">Status Notes:</p>
                  <p className="text-sm text-gray-300">{conversation.status_notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleStatusUpdate(conversation.id, 'resolved')}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                  >
                    Mark Resolved
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(conversation.id, 'active')}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                  >
                    Mark Active
                  </button>
                </div>
                <button
                  onClick={() => setExpandedConversation(
                    expandedConversation === conversation.id ? null : conversation.id
                  )}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {expandedConversation === conversation.id ? 'Hide Details' : 'Show Details'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View All Link */}
      {!isLoading && !error && pendingConversations.length > 0 && totalPending > pendingConversations.length && (
        <div className="p-4 border-t border-gray-700 text-center">
          <a
            href="/conversations?status=pending"
            className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
          >
            View All {totalPending} Pending Conversations →
          </a>
        </div>
      )}
    </div>
  );
};

export default PendingReviewSection;
